/**
 * Shared React-Query options for the dashboard "what's happening today" panel.
 *
 * The dashboard prefetch (lib/prefetch/entries/dashboard.ts) seeds caches that
 * the live hook (pages/dashboard/hooks/useDashboardData.ts useCalendarEvents)
 * later reads. React Query matches them by hashing the FULL query key — filter
 * groups AND sort/order/limit/options — so both sides MUST build their options
 * through these builders. Replicating any option at a call site reintroduces
 * the silent cache-key drift this module exists to prevent (a drifted key =
 * the prefetch is ignored and the panel cold-fetches with a visible delay).
 *
 * Both builders return sentinel query options (`queryOptions<CrmObjectListSentinelPage>`):
 * React Query caches only the matching ids/pagination metadata, while object
 * bodies live in the CRM store.
 */

import { crmObjectListSentinelQuery } from '@/lib/crm/store';
import {
  buildTodayReminderFilters,
  buildTodayTaskFilters,
} from '@/lib/dates/dashboardDateRange';
import {
  TASK_OWNER_ASSOCIATION_TYPE_NAME,
  TASK_STAGE_ASSOCIATION_TYPE_NAME,
} from '@/pages/calendar/utils/taskUtils';
import type { EntityTypeName, FilterGroup } from '@/types/crm';

export { LOADING_FALLBACK_TIMEZONE } from '@/lib/time/loadingFallbackTimezone';

/**
 * Association projections for the dashboard's two list queries.
 *
 * These replace `include_associated_objects: true`, which asks the backend for
 * EVERY association type on the entity. CRMObjectClient.list is explicit that
 * callers "MUST pass `include_associations` with the specific types they need
 * (never request all association types from the frontend)" — the all-types path
 * also makes the backend run the association-target accessibility fan-out over
 * every edge, which it notes dominates response time on high-degree entities.
 * `ahoy_meeting` is the highest-degree entity we render.
 *
 * Measured against a 43-meeting dashboard on a local proxy: all-types returned
 * 17 associated objects in a 127 KB payload; this projection returns 9 in 57 KB.
 *
 * These MUST stay in step with quickfetch's `primary_association_type_names`
 * (apps/quickfetch/views.py). quickfetch composes the cold-load payload server
 * side while this projection shapes the live refetch — if the two disagree, a
 * meeting object changes shape between first paint and refetch, which is the
 * class of bug that produced the wrong CTA this PR started from.
 *
 * NOTE on `properties`: the backend UNIONS the per-type property lists and
 * applies the union to every associated object in one batch fetch
 * (object_viewsets.py, `all_requested_props`), despite the per-type syntax. So
 * `{ properties: [] }` means "this type adds nothing to the union", NOT "fetch
 * this type's targets without properties". Tracked in #10442.
 */
export const DASHBOARD_MEETING_ASSOCIATIONS: Record<
  string,
  { properties: string[] }
> = {
  // Consumed by mapAssociatedUsers -> CalendarEvent.users.
  ahoy_meeting_to_user: {
    properties: ['ahoy_display_name', 'ahoy_avatar_url'],
  },
  // Existence only. hasReadableMeetingNotes requires the target to RESOLVE in
  // associated_objects rather than merely be referenced by an edge, because the
  // backend permission-checks only the association's source — so an edge alone
  // would leak the existence of a summary the user cannot read. No body needed.
  ahoy_meeting_to_meeting_notes: { properties: [] },
};

export const DASHBOARD_REMINDER_ASSOCIATIONS: Record<
  string,
  { properties: string[] }
> = {
  // Drives the reminder's title and its navigation target (reminderUtils.ts).
  ahoy_reminder_to_subject: { properties: ['ahoy_display_name'] },
  // Transitive owner, materialized from the subject (reminderUtils.ts).
  ahoy_reminder_to_owner: { properties: [] },
};

/**
 * Association projection for the dashboard's tasks list query.
 *
 * ahoy_task_to_owner and ahoy_task_to_task_pipeline_stage are delivered by
 * the sibling "Task associations, pipeline expand, permissions" issue — see
 * taskUtils.ts's TASK_OWNER_ASSOCIATION_TYPE_NAME /
 * TASK_STAGE_ASSOCIATION_TYPE_NAME, the single source of truth for the
 * literal names.
 *
 * Owner narrowing (isTaskVisibleToUser, taskUtils.ts) and completion status
 * (current pipeline stage) only need the edge itself, not the target's
 * properties — see the `properties` NOTE on DASHBOARD_MEETING_ASSOCIATIONS
 * above for why `{ properties: [] }` is correct here.
 */
export const DASHBOARD_TASK_ASSOCIATIONS: Record<
  string,
  { properties: string[] }
> = {
  [TASK_OWNER_ASSOCIATION_TYPE_NAME]: { properties: [] },
  [TASK_STAGE_ASSOCIATION_TYPE_NAME]: { properties: [] },
};

/**
 * Query options for today's meetings (ahoy_meeting).
 *
 * Filter groups are caller-supplied because they depend on per-user inputs
 * (integration entity ids, user entity id, and the per-user hidden-meeting
 * set) — build them with buildTodayMeetingsFilters. Everything else is fixed
 * here. Because the hidden set is embedded in filterGroups, a dismiss/undismiss
 * that changes it produces a different queryKey here — see
 * useDismissMeeting.ts for how the new key is seeded to avoid a cold refetch.
 */
export function todayMeetingsQueryOptions(
  orgId: number,
  filterGroups: FilterGroup[]
) {
  return crmObjectListSentinelQuery(
    orgId,
    'ahoy_meeting',
    {
      filter_groups: filterGroups,
      sort: 'ahoy_start_time',
      order: 'asc',
    },
    { include_associations: DASHBOARD_MEETING_ASSOCIATIONS }
  );
}

/**
 * Query options for today's reminders (ahoy_reminder).
 *
 * Date strings are caller-supplied (from getDashboardReminderDateRange) rather
 * than computed here, because the hook memoizes them once per mount while the
 * prefetch computes them per execution — computing inside this builder would
 * silently change the hook's midnight-rollover behavior.
 *
 * userEntityId is caller-supplied (like meetings) because the React-Query cache
 * key includes it — the prefetch and live-query sides must pass the same value
 * (persisted/fresh entity id or null) so the hook adopts the prefetch's cache
 * entry instead of cold-fetching.
 */
export function todayRemindersQueryOptions(
  orgId: number,
  todayDateOnly: string,
  tomorrowDateOnly: string,
  userEntityId: string | null
) {
  return crmObjectListSentinelQuery(
    orgId,
    'ahoy_reminder',
    {
      filter_groups: buildTodayReminderFilters(
        todayDateOnly,
        tomorrowDateOnly,
        userEntityId
      ),
      sort: 'ahoy_start_date',
      order: 'asc',
      include_total_count: false,
      limit: 50,
    },
    { include_associations: DASHBOARD_REMINDER_ASSOCIATIONS }
  );
}

/**
 * Query options for today's tasks (ahoy_task).
 *
 * Date strings/instants are caller-supplied (from getDashboardDateRange and
 * getDashboardReminderDateRange) rather than computed here, matching
 * todayRemindersQueryOptions — see that function's doc for why.
 *
 * ahoy_task is not yet in SYSTEM_ENTITY_TYPE_NAMES (types/crm.ts); the cast
 * mirrors the established pattern for entity types pending registry
 * addition (see e.g. usePipelineFooterConfig.ts, TaskRecordOverview.tsx).
 */
export function todayTasksQueryOptions(
  orgId: number,
  todayDateOnly: string,
  tomorrowDateOnly: string,
  todayStartISO: string,
  tomorrowStartISO: string
) {
  return crmObjectListSentinelQuery(
    orgId,
    'ahoy_task' as EntityTypeName,
    {
      filter_groups: buildTodayTaskFilters(
        todayDateOnly,
        tomorrowDateOnly,
        todayStartISO,
        tomorrowStartISO
      ),
      sort: 'ahoy_datetime',
      order: 'asc',
      include_total_count: false,
      limit: 50,
    },
    { include_associations: DASHBOARD_TASK_ASSOCIATIONS }
  );
}
